import {ChatInput, type ChatInputProps, type ChatInputRef} from '@github-ui/copilot-chat/components/ChatInput'
import {useEntitlement} from '@github-ui/copilot-chat/components/quota/EntitlementContext'
import {useNavigateToNewThread} from '@github-ui/copilot-chat/hooks/use-navigate-to-new-thread'
import {useQuotaBanner} from '@github-ui/copilot-chat/hooks/use-quota-banner'
import {useSelectedCopilotSpaceId} from '@github-ui/copilot-chat/hooks/use-selected-custom-copilot-id'
import {useSSOOrgNames} from '@github-ui/copilot-chat/hooks/use-sso-org-names'
import {isFileReference, referencesAreEqual} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import {
  type CopilotChatReference,
  CopilotLicenseType,
  ModeSupportName,
} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {useChatStateValues} from '@github-ui/copilot-chat/utils/CopilotChatContext'
import {sendEvent} from '@github-ui/hydro-analytics'
import type {AnchorSide} from '@primer/behaviors'
import {CommentIcon} from '@primer/octicons-react'
import {useCallback, useMemo, useState} from 'react'

import {useFigmaAuthUrl} from '../hooks/use-figma-auth-url'
import {useOnReferenceSelect} from '../hooks/use-on-reference-select'
import {getItemVersionForReference} from './ContentPreview/content-preview-types'
import {useContentPreview} from './ContentPreview/ContentPreviewContext'
import styles from './Layout.module.css'
import {NotificationsSection} from './NotificationsSection'

interface ChatInputContainerProps {
  activeModeName?: string
  onClickMode?: (modeName: string) => void
  textAreaRef: React.RefObject<HTMLTextAreaElement | null>
  chatInputRef?: React.RefObject<ChatInputRef | null>
  handleUserSubmit: (content: string, additionalReferences?: CopilotChatReference[]) => Promise<void>
  nextMessageIndex: number
  containerRef?: React.RefObject<HTMLFormElement | null>
  isNewConversation?: boolean
  onChatCommandChange?: ChatInputProps['onChatCommandChange']
  slashCommandsEnabled?: ChatInputProps['slashCommandsEnabled']
  showDisabledInputOnQuotaExceeded?: boolean
  attachmentMenuSide?: AnchorSide
  /** Force icon-only mode in the toolbar, useful for container-based responsive behavior */
  forceIconOnly?: boolean
}

export function ChatInputContainer({
  activeModeName,
  onClickMode,
  textAreaRef,
  handleUserSubmit,
  nextMessageIndex,
  chatInputRef,
  isNewConversation,
  onChatCommandChange,
  containerRef,
  slashCommandsEnabled,
  showDisabledInputOnQuotaExceeded = false,
  attachmentMenuSide,
  forceIconOnly,
}: ChatInputContainerProps) {
  const {streamingMessage} = useChatStateValues('streamingMessage')
  const {chatQuotaExceeded, isLicensedLimited, licenseType, tokenBasedBillingEnabled} = useEntitlement()

  // Check if the user has missing SSO organizations
  const {needsSsoAuthentication} = useSSOOrgNames()
  const selectedCopilotSpaceId = useSelectedCopilotSpaceId()
  const hideChatInput = needsSsoAuthentication && selectedCopilotSpaceId

  const navigateToNewThread = useNavigateToNewThread()
  const figmaAuthUrl = useFigmaAuthUrl()
  const [inputText, setInputText] = useState('')

  const handleInputReferenceSelect = useOnReferenceSelect({messageIndex: nextMessageIndex})

  const {versionedItems, items, promoteTab} = useContentPreview()

  const existingFileNames = useMemo(() => {
    const result = new Set<string>()
    for (const [, item] of items) if (item.type === 'file') result.add(item.name)
    return result
  }, [items])

  const handlePromoteImplicitReference = useCallback(
    (reference: CopilotChatReference) => {
      if (!isFileReference(reference)) return
      for (const [id, item] of items) {
        if (item.type === 'file' && isFileReference(item.reference) && referencesAreEqual(item.reference, reference)) {
          promoteTab(id)
          sendEvent('dotcom_chat.activate', {
            target: 'IMPLICIT_REFERENCE_PROMOTED',
            mode: 'immersive',
            source: 'reference_pill_click',
          })
          break
        }
      }
    },
    [items, promoteTab],
  )

  const quotaBanner = useQuotaBanner()
  const quotaExceeded = tokenBasedBillingEnabled
    ? quotaBanner.show && quotaBanner.isBlocking
    : isLicensedLimited && chatQuotaExceeded
  const canUseChat = !quotaExceeded && !hideChatInput
  const shouldShowInput = canUseChat || (quotaExceeded && showDisabledInputOnQuotaExceeded)

  return (
    <div className={styles.chatInputContainer}>
      <NotificationsSection inputText={inputText} />
      {shouldShowInput && (
        <ChatInput
          activeModeName={activeModeName}
          onClickMode={onClickMode}
          modes={[
            {
              name: ModeSupportName.Ask,
              supportsAttachments: true,
              supportsModelSwitching: licenseType !== CopilotLicenseType.Unlicensed,
              supportsSpaces: true,
              supportsAutocompletion: true,
              icon: CommentIcon,
              placeholder: 'Ask anything or type @ to add context',
            },
          ]}
          onNewThreadSelected={navigateToNewThread}
          textAreaRef={textAreaRef}
          chatInputRef={chatInputRef}
          containerRef={containerRef}
          onSubmit={handleUserSubmit}
          isStreaming={!!streamingMessage}
          size="large"
          onSelectReference={handleInputReferenceSelect}
          onPromoteImplicitReference={handlePromoteImplicitReference}
          getReferenceVersion={reference =>
            getItemVersionForReference(reference, nextMessageIndex, versionedItems, items)
          }
          existingFileNames={existingFileNames}
          figmaAuthUrl={figmaAuthUrl}
          isNewConversation={isNewConversation}
          onChatCommandChange={onChatCommandChange}
          slashCommandsEnabled={slashCommandsEnabled}
          disabled={quotaExceeded}
          onTextChange={setInputText}
          attachmentMenuSide={attachmentMenuSide}
          forceIconOnly={forceIconOnly}
        />
      )}
    </div>
  )
}
